import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  AccountBalance,
  Add,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  AccountBalanceWallet,
  CreditCard,
  Savings,
  MoneyOff,
  Assessment,
  Refresh,
  Visibility,
  Edit
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`banking-tabpanel-${index}`}
    aria-labelledby={`banking-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const BankingManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [withdrawalDialogOpen, setWithdrawalDialogOpen] = useState(false);
  const [cashCollectionDialogOpen, setCashCollectionDialogOpen] = useState(false);

  // Form states
  const [newAccount, setNewAccount] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    account_type: 'Current',
    balance: ''
  });

  const [newDeposit, setNewDeposit] = useState({
    account_id: '',
    amount: '',
    description: '',
    deposit_date: new Date().toISOString().split('T')[0],
    reference_number: ''
  });

  const [newWithdrawal, setNewWithdrawal] = useState({
    account_id: '',
    amount: '',
    description: '',
    withdrawal_date: new Date().toISOString().split('T')[0],
    reference_number: ''
  });

  const [newCashCollection, setNewCashCollection] = useState({
    amount: '',
    description: '',
    source_type: 'sale',
    transaction_date: new Date().toISOString().split('T')[0]
  });

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsRes, depositsRes, withdrawalsRes, cashFlowRes, summaryRes] = await Promise.all([
        fetch('/api/banking/accounts'),
        fetch('/api/banking/deposits'),
        fetch('/api/banking/withdrawals'),
        fetch('/api/banking/cash-flow'),
        fetch('/api/banking/summary')
      ]);

      const [accountsData, depositsData, withdrawalsData, cashFlowData, summaryData] = await Promise.all([
        accountsRes.json(),
        depositsRes.json(),
        withdrawalsRes.json(),
        cashFlowRes.json(),
        summaryRes.json()
      ]);

      setAccounts(accountsData);
      setDeposits(depositsData);
      setWithdrawals(withdrawalsData);
      setCashFlow(cashFlowData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading banking data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle create account
  const handleCreateAccount = async () => {
    try {
      const response = await fetch('/api/banking/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAccount)
      });

      if (response.ok) {
        setAccountDialogOpen(false);
        setNewAccount({
          account_name: '',
          bank_name: '',
          account_number: '',
          account_type: 'Current',
          balance: ''
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  // Handle create deposit
  const handleCreateDeposit = async () => {
    try {
      const response = await fetch('/api/banking/deposits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDeposit)
      });

      if (response.ok) {
        setDepositDialogOpen(false);
        setNewDeposit({
          account_id: '',
          amount: '',
          description: '',
          deposit_date: new Date().toISOString().split('T')[0],
          reference_number: ''
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating deposit:', error);
    }
  };

  // Handle create withdrawal
  const handleCreateWithdrawal = async () => {
    try {
      const response = await fetch('/api/banking/withdrawals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newWithdrawal)
      });

      if (response.ok) {
        setWithdrawalDialogOpen(false);
        setNewWithdrawal({
          account_id: '',
          amount: '',
          description: '',
          withdrawal_date: new Date().toISOString().split('T')[0],
          reference_number: ''
        });
        loadData();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create withdrawal');
      }
    } catch (error) {
      console.error('Error creating withdrawal:', error);
    }
  };

  // Handle cash collection
  const handleCreateCashCollection = async () => {
    try {
      const response = await fetch('/api/banking/cash-collection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCashCollection)
      });

      if (response.ok) {
        setCashCollectionDialogOpen(false);
        setNewCashCollection({
          amount: '',
          description: '',
          source_type: 'sale',
          transaction_date: new Date().toISOString().split('T')[0]
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating cash collection:', error);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'cash_collection': return <TrendingUp color="success" />;
      case 'bank_deposit': return <TrendingDown color="primary" />;
      case 'bank_withdrawal': return <TrendingUp color="warning" />;
      default: return <AttachMoney />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'cash_collection': return 'success';
      case 'bank_deposit': return 'primary';
      case 'bank_withdrawal': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Banking & Cash Management</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Cash on Hand</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(summary.cash_balance || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalance color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Bank Balance</Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(summary.total_bank_balance || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Deposits</Typography>
                  <Typography variant="h6" color="info.main">
                    {formatCurrency(summary.total_deposits || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDown color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Withdrawals</Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(summary.total_withdrawals || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Bank Accounts" />
            <Tab label="Deposits" />
            <Tab label="Withdrawals" />
            <Tab label="Cash Flow" />
          </Tabs>
        </Box>

        {/* Bank Accounts Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Bank Accounts</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAccountDialogOpen(true)}
            >
              Add Account
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.account_name}</TableCell>
                    <TableCell>{account.bank_name}</TableCell>
                    <TableCell>{account.account_number}</TableCell>
                    <TableCell>
                      <Chip 
                        label={account.account_type}
                        color={account.account_type === 'Current' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(account.balance)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.is_active ? 'Active' : 'Inactive'}
                        color={account.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Deposits Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Bank Deposits</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDepositDialogOpen(true)}
              disabled={accounts.length === 0}
            >
              Record Deposit
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((deposit) => (
                  <TableRow key={deposit.id}>
                    <TableCell>{new Date(deposit.deposit_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {deposit.account_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {deposit.bank_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{deposit.description}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(deposit.amount)}
                    </TableCell>
                    <TableCell>{deposit.reference_number || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Withdrawals Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Bank Withdrawals</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setWithdrawalDialogOpen(true)}
              disabled={accounts.length === 0}
            >
              Record Withdrawal
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id}>
                    <TableCell>{new Date(withdrawal.withdrawal_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {withdrawal.account_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {withdrawal.bank_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{withdrawal.description}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {formatCurrency(withdrawal.amount)}
                    </TableCell>
                    <TableCell>{withdrawal.reference_number || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Cash Flow Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Cash Flow Transactions</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCashCollectionDialogOpen(true)}
            >
              Record Cash Collection
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cashFlow.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{new Date(transaction.transaction_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTransactionIcon(transaction.transaction_type)}
                        <Chip 
                          label={transaction.transaction_type.replace('_', ' ')}
                          color={getTransactionColor(transaction.transaction_type)}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: transaction.amount >= 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.source_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Add Account Dialog */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Bank Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Account Name"
              value={newAccount.account_name}
              onChange={(e) => setNewAccount(prev => ({ ...prev, account_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Bank Name"
              value={newAccount.bank_name}
              onChange={(e) => setNewAccount(prev => ({ ...prev, bank_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Account Number"
              value={newAccount.account_number}
              onChange={(e) => setNewAccount(prev => ({ ...prev, account_number: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={newAccount.account_type}
                onChange={(e) => setNewAccount(prev => ({ ...prev, account_type: e.target.value }))}
                label="Account Type"
              >
                <MenuItem value="Current">Current</MenuItem>
                <MenuItem value="Savings">Savings</MenuItem>
                <MenuItem value="Fixed Deposit">Fixed Deposit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Initial Balance"
              type="number"
              value={newAccount.balance}
              onChange={(e) => setNewAccount(prev => ({ ...prev, balance: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">TZS</InputAdornment>
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateAccount} 
            variant="contained"
            disabled={!newAccount.account_name || !newAccount.bank_name}
          >
            Create Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Deposit Dialog */}
      <Dialog open={depositDialogOpen} onClose={() => setDepositDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Bank Deposit</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Bank Account</InputLabel>
              <Select
                value={newDeposit.account_id}
                onChange={(e) => setNewDeposit(prev => ({ ...prev, account_id: e.target.value }))}
                label="Bank Account"
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.account_name} - {account.bank_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              value={newDeposit.amount}
              onChange={(e) => setNewDeposit(prev => ({ ...prev, amount: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">TZS</InputAdornment>
              }}
            />
            <TextField
              label="Description"
              value={newDeposit.description}
              onChange={(e) => setNewDeposit(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Deposit Date"
              type="date"
              value={newDeposit.deposit_date}
              onChange={(e) => setNewDeposit(prev => ({ ...prev, deposit_date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Reference Number"
              value={newDeposit.reference_number}
              onChange={(e) => setNewDeposit(prev => ({ ...prev, reference_number: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateDeposit} 
            variant="contained"
            disabled={!newDeposit.account_id || !newDeposit.amount || !newDeposit.description}
          >
            Record Deposit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Withdrawal Dialog */}
      <Dialog open={withdrawalDialogOpen} onClose={() => setWithdrawalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Bank Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Bank Account</InputLabel>
              <Select
                value={newWithdrawal.account_id}
                onChange={(e) => setNewWithdrawal(prev => ({ ...prev, account_id: e.target.value }))}
                label="Bank Account"
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.account_name} - {account.bank_name} (Balance: {formatCurrency(account.balance)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              value={newWithdrawal.amount}
              onChange={(e) => setNewWithdrawal(prev => ({ ...prev, amount: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">TZS</InputAdornment>
              }}
            />
            <TextField
              label="Description"
              value={newWithdrawal.description}
              onChange={(e) => setNewWithdrawal(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Withdrawal Date"
              type="date"
              value={newWithdrawal.withdrawal_date}
              onChange={(e) => setNewWithdrawal(prev => ({ ...prev, withdrawal_date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Reference Number"
              value={newWithdrawal.reference_number}
              onChange={(e) => setNewWithdrawal(prev => ({ ...prev, reference_number: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateWithdrawal} 
            variant="contained"
            disabled={!newWithdrawal.account_id || !newWithdrawal.amount || !newWithdrawal.description}
          >
            Record Withdrawal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Cash Collection Dialog */}
      <Dialog open={cashCollectionDialogOpen} onClose={() => setCashCollectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Cash Collection</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              value={newCashCollection.amount}
              onChange={(e) => setNewCashCollection(prev => ({ ...prev, amount: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">TZS</InputAdornment>
              }}
            />
            <TextField
              label="Description"
              value={newCashCollection.description}
              onChange={(e) => setNewCashCollection(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={newCashCollection.source_type}
                onChange={(e) => setNewCashCollection(prev => ({ ...prev, source_type: e.target.value }))}
                label="Source Type"
              >
                <MenuItem value="sale">Sale</MenuItem>
                <MenuItem value="egg_sale">Egg Sale</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Collection Date"
              type="date"
              value={newCashCollection.transaction_date}
              onChange={(e) => setNewCashCollection(prev => ({ ...prev, transaction_date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashCollectionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateCashCollection} 
            variant="contained"
            disabled={!newCashCollection.amount || !newCashCollection.description}
          >
            Record Collection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingManagement;
